import { useTranslation } from 'react-i18next';
import { useState, FormEvent, ChangeEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Modal from 'shared/overlays/Modal';
import { useUser } from 'app/state/contexts/UserContext';
import { useUsersInOrganization } from 'entities/user';
import { useCreateOrganizationUser } from 'entities/registry';
import UserList from './UserList';

interface AttributeRole {
  attributeId: number;
  roles: { role: string }[];
}

interface OrganizationUser {
  id: string;
  firstName: string;
  lastName: string;
  userName: string;
  lastVisitTime?: string;
  attributeRoles: AttributeRole[];
}

const Users = () => {
  const { t } = useTranslation(['common', 'generic', 'users']);

  const { selectedOrganization } = useUser();
  const queryClient = useQueryClient();
  const { data: users = [] } = useUsersInOrganization(selectedOrganization ?? '');
  const { mutate: createOrganizationUser } = useCreateOrganizationUser();
  const [showModal, setShowModal] = useState(false);

  const [userName, setUserName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [isNewAdmin, setIsNewAdmin] = useState(false);
  const roleAdmin = 'ROLE_ORGANIZATION_ADMIN';
  const roleUser = 'ROLE_ORGANIZATION_USER';
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const filterUsers = (users: OrganizationUser[] = [], currentRole: string) => {
    return users?.filter((user) => {
      return user.attributeRoles.find((role) => role.attributeId === selectedOrganization && role.roles.find((r) => r.role === currentRole));
    });
  };

  const resetForm = () => {
    setUserName('');
    setFirstName('');
    setLastName('');
    setPassword('');
  };

  const createUser = (event: FormEvent) => {
    event.preventDefault();
    if (!userName || (password && (!userName || !firstName || !lastName))) {
      setShowErrorMessage(true);
      return;
    }

    toggleModal();
    setShowErrorMessage(false);
    const newUser = {
      userName: userName,
      password: password,
      firstName: firstName,
      lastName: lastName,
      forceChangePassword: true,
      email: [userName],
    };

    createOrganizationUser(
      {
        newUser,
        userName,
        role: isNewAdmin ? roleAdmin : roleUser,
        roleId: isNewAdmin ? 4 : 5,
        organizationId: selectedOrganization as number,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['organizationId', selectedOrganization],
          });
          resetForm();
        },
      }
    );
  };

  const handleCancel = () => {
    toggleModal();
    setShowErrorMessage(false);
    resetForm();
  };

  const handleAddUser = (isAdmin: boolean) => {
    toggleModal();
    setIsNewAdmin(isAdmin);
  };

  return (
    <div>
      <UserList listTitle={t('users:customer-admin.list.title')} users={filterUsers(users as OrganizationUser[], roleAdmin)} onAdd={handleAddUser} isAdmin />
      <UserList listTitle={t('users:user-rights.list.title')} users={filterUsers(users as OrganizationUser[], roleUser)} onAdd={handleAddUser} />

      <Modal show={showModal} onClose={toggleModal}>
        <form onSubmit={createUser}>
          <div className="mb-4">
            <label htmlFor="username" className="form-label">
              {t('users:new-user.modal.field.username')}
            </label>
            <input
              type="text"
              name="username"
              value={userName}
              placeholder=""
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setUserName(event.target.value);
              }}
              className="form-control"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="firstname" className="form-label">
              {t('users:new-user.modal.field.firstname')}
            </label>
            <input
              type="text"
              name="firstname"
              value={firstName}
              placeholder=""
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setFirstName(event.target.value);
              }}
              className="form-control"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="lastname" className="form-label">
              {t('users:new-user.modal.field.lastname')}
            </label>
            <input
              type="text"
              name="lastname"
              value={lastName}
              placeholder=""
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setLastName(event.target.value);
              }}
              className="form-control"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="password" className="form-label">
              {t('users:new-user.modal.field.password')}
            </label>
            <input
              type="password"
              name="password"
              value={password}
              placeholder=""
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPassword(event.target.value);
              }}
              className="form-control"
            />
            {showErrorMessage ? <p className="form-validation-error-message">{t('generic:form-validation.error-message')}</p> : null}
          </div>
          <div className="d-flex justify-content-end">
            <button type="button" className="button clear" onClick={handleCancel}>
              {t('common:button.cancel')}
            </button>
            <button type="submit" className="button primary">
              {t('common:button.save')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Users;
